import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useSelector } from 'react-redux'

import { selectCollegePreferences } from 'store/user'

/**
 * When preferences change, the sort order of posts-search neds to be refreshed
 * To refresh sort order, send a "refreshSort" param to the posts-search endpoint
 * We should only do this if the user isn't in the middle of a search
 * and their "Preferences" have changed!
 */

const KEY = 'scoir-posts-search-needs-refresh-sort'

export const enableRefreshSort = () => {
  localStorage.setItem(KEY, true)
}

export const getRefreshSort = () => {
  return !!localStorage.getItem(KEY)
}

export const resetRefreshSort = () => {
  localStorage.removeItem(KEY)
}

/**
 * Use this hook to maintain the need to refresh sort for posts page
 */
export const useRefreshSort = () => {
  const refreshSort = getRefreshSort()
  const needsRefreshSort = useRef(refreshSort)

  const onResetRefreshSort = useCallback(() => {
    resetRefreshSort()
    needsRefreshSort.current = false
  }, [])

  return {
    needsRefreshSort: needsRefreshSort.current,
    onResetRefreshSort,
  }
}

/**
 * Use this hook at a high level in the virtual dom to enable the refresh
 */
export const useEnableRefreshSortOnPrefsChange = () => {
  const prefs = useSelector(selectCollegePreferences)
  const stringified = useMemo(() => JSON.stringify(prefs), [prefs])
  const lastStringified = useRef(stringified)

  useEffect(() => {
    const prefsChanged = lastStringified.current !== stringified
    if (prefsChanged) {
      lastStringified.current = stringified
      enableRefreshSort()
    }
  }, [stringified])
}
